import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import BackButton from "../components/BackButton";
import { CRICKET_API_URL } from "../config";
import { ContestContext } from "../context/ContestContext";
import { UserContext } from "../context/UserContext";

const parseMatchTime = (value) => {
    if (!value) return null;

    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === "number") {
        const normalizedValue = value < 10000000000 ? value * 1000 : value;
        const parsed = new Date(normalizedValue);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof value === "string") {
        const parsed = new Date(value);
        if (!Number.isNaN(parsed.getTime())) return parsed;

        const fallback = new Date(value.replace(" ", "T"));
        return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    return null;
};

/* =========================================
    ✅ COMPONENT: Match Timer
========================================= */
const MatchTimer = ({ startTime }) => {
    const [timeLeft, setTimeLeft] = useState("");
    useEffect(() => {
        const calculateTimeLeft = () => {
            if (!startTime || startTime === "TBD") return "TBD";
            const parsedStartTime = parseMatchTime(startTime);
            const start = parsedStartTime?.getTime();
            const now = new Date().getTime();

            if (!start || Number.isNaN(start)) return "TBD";

            const diff = start - now;
            if (diff <= 0) return "Live";
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            return `${hours}h ${minutes}m ${seconds}s`;
        };
        setTimeLeft(calculateTimeLeft());
        const timer = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
        return () => clearInterval(timer);
    }, [startTime]);
    return <span style={{ fontVariantNumeric: "tabular-nums" }}>{timeLeft}</span>;
};

/* =========================================
    ✅ HELPER: Random Avatar Color
========================================= */
const getAvatarColor = (name) => {
    const colors = ["#ef4444", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899"];
    return colors[(name ? name.charCodeAt(0) : 0) % colors.length];
};

const shouldShowLeaderboardWinnings = ({ status = "", settled = false } = {}) => {
    if (Boolean(settled)) return true;

    const normalizedStatus = String(status || "").trim().toLowerCase();

    return (
        normalizedStatus === "completed" ||
        normalizedStatus === "settled" ||
        normalizedStatus.includes("completed") ||
        normalizedStatus.includes("finished") ||
        normalizedStatus.includes("result")
    );
};

const getStoredUserProfile = () => {
    try {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    } catch (error) {
        return null;
    }
};

const normalizeUserId = (value = "") => String(value || "").trim();

function ContestDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { contestId } = useParams();

    // Contexts
    const { getContestDetail, joinContest, loading: contextLoading } = useContext(ContestContext);
    const { user: currentUser, fetchUserData } = useContext(UserContext);

    // ✅ Fix: currentContestId defined at top level
    const currentContestId = location.state?.contest?._id || contestId;

    // States
    const [contest, setContest] = useState(location.state?.contest || null);
    const [matchData] = useState(location.state?.matchData || null);
    const [myTeams, setMyTeams] = useState([]);
    const [leaderboard, setLeaderboard] = useState([]);
    const [localLoading, setLocalLoading] = useState(!contest);
    const [activeTab, setActiveTab] = useState("leaderboard");
    const [joining, setJoining] = useState(false);

    // User Info
    const localUser = useMemo(() => getStoredUserProfile(), []);
    const currentUserId = useMemo(
        () =>
            normalizeUserId(
                currentUser?._id ||
                currentUser?.id ||
                localUser?._id ||
                localUser?.id ||
                localUser?.userId ||
                localUser?.user?._id ||
                localStorage.getItem("userId") ||
                localStorage.getItem("_id")
            ),
        [currentUser, localUser]
    );
    const currentUserName =
        currentUser?.userName ||
        currentUser?.name ||
        localUser?.userName ||
        localUser?.name ||
        localUser?.user?.name ||
        "";
    const resolvedCurrentUserName = currentUserName || "Player";
    const currentUserNameKey = String(currentUser?.userName || currentUserName || "").trim().toLowerCase();
    const isCurrentUser = useCallback(
        (userId, userName = "") =>
            normalizeUserId(userId) === currentUserId ||
            (Boolean(currentUserNameKey) &&
                String(userName || "").trim().toLowerCase() === currentUserNameKey),
        [currentUserId, currentUserNameKey]
    );

    // --- FETCH DATA ---
    useEffect(() => {
        const initData = async () => {
            const token = localStorage.getItem("token");

            try {
                // 1. Fetch Contest Details if not in state
                if (!contest) {
                    const data = await getContestDetail(currentContestId);
                    if (data) setContest(data);
                }

                // 2. Fetch Leaderboard from Cricket Server
                const lbRes = await axios.get(`${CRICKET_API_URL}/api/cricket/contests/leaderboard/${currentContestId}`);
                if (lbRes.data.success) {
                    const participants = lbRes.data.data || [];
                    const formattedLB = participants.map(p => ({
                        name: p.userName || "Player",
                        userName: p.userName || "Player",
                        userId: p.userId,
                        teamName: p.userTeamId?.teamName || `Team ${p.userTeamId?.teamNumber || 1}`,
                        winnings: Number(p.winnings || p.prize || 0),
                        isUser: isCurrentUser(p.userId, p.userName),
                        avatar: p.userAvatar || p.dp || null
                    }));

                    // 🔥 FIX 1: LEADERBOARD SORTING
                    formattedLB.sort((a, b) => {
                        if (a.isUser) return -1;
                        if (b.isUser) return 1;
                        return 0;
                    });

                    setLeaderboard(formattedLB);
                }

                // 3. Fetch My Teams
                const mId = matchData?.matchId || contest?.matchId || matchData?.id;
                if (mId && currentUserId && token) {
                    const teamRes = await axios.get(`${CRICKET_API_URL}/api/cricket/my-teams/${currentUserId}/${mId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setMyTeams(teamRes.data || []);
                }
            } catch (err) {
                console.error("Initialization Error:", err);
            } finally {
                setLocalLoading(false);
            }
        };

        if (currentContestId) initData();
    }, [currentContestId, contest, matchData, getContestDetail, currentUserId, isCurrentUser]);

    // --- JOIN LOGIC ---
    const handleJoinContest = async () => {
        const token = localStorage.getItem("token");

        if (!token) {
            alert("Please Login first!");
            navigate("/login");
            return;
        }

        if (myTeams.length === 0) {
            alert("Create a team first!");
            return navigate("/create-team", { state: { matchData, contestToJoin: contest } });
        }

        if (!window.confirm(`Join for ₹${contest.entryFee}?`)) return;

        setJoining(true);
        try {
            const res = await joinContest(
                currentUserId,
                resolvedCurrentUserName,
                contest?._id || currentContestId,
                myTeams[0]._id,
                matchData?.matchId || contest?.matchId,
                token
            );

            if (res.success) {
                alert("🎉 Contest Joined!");
                await fetchUserData();

                // Instant UI update for Leaderboard
                const newUserEntry = {
                    name: resolvedCurrentUserName,
                    userName: resolvedCurrentUserName,
                    teamName: myTeams[0].teamName || "T1",
                    isUser: true,
                    userId: currentUserId,
                    avatar: localUser.avatar || localUser.dp || null
                };

                setContest(prev => ({
                    ...prev,
                    remainingSpots: (prev?.remainingSpots || 1) - 1,
                    spotsFilled: (prev?.spotsFilled || 0) + 1
                }));

                setLeaderboard(prev => [newUserEntry, ...prev.filter(p => !p.isUser)]);
            }
        } catch (msg) {
            alert("Join Failed: " + msg);
        } finally {
            setJoining(false);
        }
    };

    const contestData = {
        ...(contest || {}),
        leaderboard,
    };

    // UI Calculations
    const progress = contest ? (((contest.totalSpots - (contest.remainingSpots || 0)) / contest.totalSpots) * 100) : 0;
    const canShowWinnings = shouldShowLeaderboardWinnings({
        status: matchData?.status || contest?.status,
        settled: matchData?.settled || contest?.settled,
    });
    const hasJoined = Boolean(
        contestData?.leaderboard?.some((participant) =>
            normalizeUserId(participant?.userId) === currentUserId ||
            normalizeUserId(participant?.userId?._id) === currentUserId ||
            (Boolean(currentUserNameKey) &&
                String(participant?.userName || participant?.name || "").trim().toLowerCase() === currentUserNameKey)
        ) ||
        contestData?.participants?.some((participant) =>
            normalizeUserId(participant?.userId) === currentUserId ||
            normalizeUserId(participant?.userId?._id) === currentUserId
        )
    );
    const isJoinDisabled = hasJoined || joining || contest?.remainingSpots <= 0;
    const joinButtonLabel = joining
        ? "Joining..."
        : hasJoined
            ? `JOINED CONTEST ₹${contest.entryFee}`
            : contest?.remainingSpots <= 0
                ? "CONTEST FULL"
                : `JOIN CONTEST ₹${contest.entryFee}`;
    const sortedLeaderboard = [...(contestData?.leaderboard || [])].sort((a, b) => {
        const aIsCurrentUser =
            normalizeUserId(a?.userId) === currentUserId ||
            normalizeUserId(a?.userId?._id) === currentUserId ||
            (Boolean(currentUserNameKey) &&
                String(a?.userName || a?.name || "").trim().toLowerCase() === currentUserNameKey);
        const bIsCurrentUser =
            normalizeUserId(b?.userId) === currentUserId ||
            normalizeUserId(b?.userId?._id) === currentUserId ||
            (Boolean(currentUserNameKey) &&
                String(b?.userName || b?.name || "").trim().toLowerCase() === currentUserNameKey);

        if (aIsCurrentUser) return -1;
        if (bIsCurrentUser) return 1;
        return 0;
    });

    // 🔥 NEW: Backend se aaye hue prize breakup ko safely uthana
    const prizeBreakup = contest?.prizeBreakup || [];

    if (localLoading || contextLoading) return (
        <div style={{ minHeight: "100vh", display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f3f4f6' }}>
            <div className="spinner-border" style={{ color: "#800000" }} role="status"></div>
        </div>
    );

    if (!contest) return <div className="p-5 text-center fw-bold text-muted">Contest not found</div>;

    return (
        <div style={{ minHeight: "100vh", background: "#f1f5f9", paddingBottom: "120px" }}>

            {/* HEADER */}
            <div className="text-white pt-3 pb-4 px-3 sticky-top shadow-sm" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", borderRadius: "0 0 25px 25px", zIndex: 100 }}>
                <div className="d-flex align-items-center gap-3">
                    <BackButton className="text-white" />
                    <div className="flex-grow-1 text-center pe-5">
                        <small className="d-block text-white-50 text-uppercase fw-bold" style={{ fontSize: "10px", letterSpacing: "1px" }}>Prize Pool</small>
                        <h2 className="fw-bold mb-0" style={{ textShadow: "0 2px 4px rgba(0,0,0,0.2)" }}>₹{contest.prizePool?.toLocaleString()}</h2>
                        <div className="badge bg-black bg-opacity-25 text-white mt-1 fw-medium border border-white border-opacity-10 px-3 py-1 shadow-sm rounded-pill">
                            <i className="bi bi-clock me-1"></i> <MatchTimer startTime={matchData?.matchStartTime || matchData?.startTime || matchData?.date} /> Left
                        </div>
                    </div>
                </div>
            </div>

            {/* PROGRESS CARD */}
            <div className="container px-3 mt-3">
                <div className="bg-white p-3 shadow-sm rounded-4 mb-3 border-0 animate-fade-in position-relative overflow-hidden">
                    <div className="d-flex justify-content-between mb-2">
                        <small className="text-danger fw-bold" style={{ fontSize: "12px" }}>
                            {contest.remainingSpots <= 0 ? <><i className="bi bi-check-circle-fill me-1 text-success"></i><span className="text-success">Contest Full</span></> : <><i className="bi bi-fire me-1"></i>{contest.remainingSpots} spots left</>}
                        </small>
                        <small className="text-muted fw-bold" style={{ fontSize: "11px" }}>{contest.totalSpots} spots</small>
                    </div>
                    <div className="progress shadow-sm" style={{ height: "6px", borderRadius: "10px", background: "#e2e8f0" }}>
                        <div className={`progress-bar ${contest.remainingSpots <= 0 ? 'bg-success' : 'bg-danger'}`}
                            style={{ width: `${progress}%`, borderRadius: "10px", background: contest.remainingSpots > 0 ? "linear-gradient(90deg, #dc3545, #ff4d4d)" : "" }}>
                        </div>
                    </div>
                </div>
            </div>

            {/* TABS */}
            <div className="container px-3 sticky-top" style={{ top: "15px", zIndex: 90 }}>
                <div className="bg-white shadow-sm rounded-pill p-1 d-flex mb-3 border border-light">
                    {["leaderboard", "winnings"].map((tab) => (
                        <div key={tab}
                            className={`flex-grow-1 text-center py-2 rounded-pill fw-bold text-uppercase transition-all ${activeTab === tab ? "bg-dark text-white shadow-sm" : "text-muted"}`}
                            style={{ cursor: "pointer", fontSize: "12px", letterSpacing: "0.5px" }}
                            onClick={() => setActiveTab(tab)}>
                            {tab}
                        </div>
                    ))}
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="container px-3">
                {activeTab === "leaderboard" && (
                    <div className="bg-white rounded-4 shadow-sm overflow-hidden border border-light">
                        <div className="bg-light px-3 py-2 border-bottom d-flex justify-content-between">
                            <small className="text-muted fw-bold" style={{ fontSize: "10px", letterSpacing: "1px" }}>PLAYER</small>
                            <small className="text-muted fw-bold" style={{ fontSize: "10px", letterSpacing: "1px" }}>TEAM</small>
                        </div>
                        {sortedLeaderboard.length === 0 ? (
                            <div className="text-center py-5 text-muted"><p className="fw-medium mb-0">Be the first to join!</p></div>
                        ) : (
                            sortedLeaderboard.map((user, idx) => (
                                <div key={idx} className={`d-flex align-items-center px-3 py-3 border-bottom border-light transition-all ${user.isUser ? "bg-warning bg-opacity-10" : ""}`}>
                                    {user.avatar ? (
                                        <img src={user.avatar} alt={user.name} className="rounded-circle me-3 border shadow-sm" style={{ width: "42px", height: "42px", objectFit: "cover" }} />
                                    ) : (
                                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3 text-white fw-bold shadow-sm border border-light"
                                            style={{ width: "42px", height: "42px", background: getAvatarColor(user.name), fontSize: "18px" }}>
                                            {user.name?.charAt(0).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex-grow-1">
                                        <h6 className="mb-0 fw-bold text-dark" style={{ fontSize: "14px" }}>
                                            {user.name}
                                            {user.isUser && <span className="badge bg-dark ms-2 rounded-pill" style={{ fontSize: '9px', padding: "3px 6px", verticalAlign: "middle" }}>YOU</span>}
                                        </h6>
                                        {canShowWinnings && user.winnings > 0 && (
                                            <div className="text-success fw-bold small mt-1">
                                                <i className="bi bi-trophy-fill me-1"></i>
                                                Won ₹{user.winnings}
                                            </div>
                                        )}
                                    </div>
                                    <div className="bg-light px-2 py-1 rounded border text-muted fw-bold shadow-sm" style={{ fontSize: '11px' }}>{user.teamName}</div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {/* 🔥 NEW: BACKEND GENERATED WINNINGS TAB */}
                {activeTab === "winnings" && (
                    <div className="bg-white rounded-4 shadow-sm overflow-hidden border border-light">
                        <div className="bg-light px-3 py-2 border-bottom d-flex justify-content-between">
                            <small className="text-muted fw-bold" style={{ fontSize: "10px", letterSpacing: "1px" }}>RANK</small>
                            <small className="text-muted fw-bold" style={{ fontSize: "10px", letterSpacing: "1px" }}>WINNINGS</small>
                        </div>

                        {prizeBreakup.length === 0 ? (
                            <div className="text-center py-5 text-muted small fw-bold">No prize breakup available</div>
                        ) : (
                            prizeBreakup.map((item, idx) => (
                                <div key={idx} className="d-flex justify-content-between align-items-center px-3 py-3 border-bottom border-light transition-all hover-bg-light">
                                    <span className="fw-bold text-muted" style={{ fontSize: "14px" }}>
                                        #{item.rankStart === item.rankEnd ? item.rankStart : `${item.rankStart}-${item.rankEnd}`}
                                    </span>
                                    {/* ContestDetail.jsx mein line 380 ke aas-paas */}
                                    <span className="fw-bold text-dark" style={{ fontSize: "15px" }}>
                                        {/* 🔥 Ye line dono keys (prize ya prizeAmount) ko handle kar legi */}
                                        ₹{(item.prizeAmount || item.prize || 0).toLocaleString()}
                                    </span>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* FIXED BOTTOM ACTION */}
            <div className="fixed-bottom p-3 bg-white border-top shadow-lg" style={{ zIndex: 1000 }}>
                {hasJoined ? (
                    <button
                        disabled
                        className="btn w-100 py-3 rounded-pill fw-bold fs-6 shadow-sm text-white text-center cursor-not-allowed"
                        style={{ background: "#6c757d", border: "none" }}
                    >
                        {`JOINED CONTEST ₹${contest.entryFee}`}
                    </button>
                ) : (
                    <button
                        className={`btn w-100 py-3 rounded-pill fw-bold fs-6 shadow-sm text-white d-flex align-items-center justify-content-center gap-2 transition-all ${isJoinDisabled ? 'bg-secondary opacity-75' : ''}`}
                        onClick={isJoinDisabled ? undefined : handleJoinContest}
                        disabled={isJoinDisabled}
                        style={
                            !joining && contest?.remainingSpots > 0
                                ? { background: "linear-gradient(135deg, #800000 0%, #dc3545 100%)", border: "none" }
                                : { border: "none" }
                        }
                    >
                        {joining ? <><span className="spinner-border spinner-border-sm me-2"></span>{joinButtonLabel}</> : joinButtonLabel}
                    </button>
                )}
            </div>

            <style>{`
                .transition-all { transition: all 0.2s ease-in-out; }
                .hover-bg-light:hover { background-color: #f8f9fa; }
                .animate-fade-in { animation: fadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1); }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
}

export default ContestDetail;
